import React, { useEffect, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
export const BlogPost = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    content: React.ReactNode;
    author: string;
    date: string;
    image: string;
    slug: string;
  }
  const blogPosts: BlogPost[] = [{
    id: 1,
    title: 'The Art & Science of Relationship Building',
    excerpt: 'When I first started in human capital development, I used to walk into networking events with one mission: meet as many people as possible.',
    content: <div className="space-y-4" data-id="element-2610">
          <p data-id="element-2611">
            When I first started in human capital development, I used to walk
            into networking events with one mission: 👉 meet as many people as
            possible.
          </p>
          <p data-id="element-2612">
            I'd leave with a stack of business cards and feel like I was
            winning. But a week later, most of those "connections" barely
            remembered who I was.
          </p>
          <p data-id="element-2613">Then one day, something clicked.</p>
          <p data-id="element-2614">
            At an event in Abuja, I stopped trying to sell myself and instead
            spent the evening asking people about their stories, their
            challenges, what excited them.
          </p>
          <p data-id="element-2615">
            One person I spoke with that night became a long-term partner — not
            because I pitched, but because I listened.
          </p>
          <p data-id="element-2616">That experience taught me the real formula:</p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2617">🔬 The Science</h3>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2618">
            <li data-id="element-2619">
              People are wired to respond to genuine interest (psychology calls
              it reciprocity).
            </li>
            <li data-id="element-2620">
              Familiarity and consistency breed trust — it's why check-ins
              matter more than cold pitches.
            </li>
            <li data-id="element-2621">
              Relationships are built on emotional memory, not transactional
              exchanges.
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2622">🎨 The Art</h3>
          <p data-id="element-2623">What I've learned: it's the small, human gestures that matter.</p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2624">
            <li data-id="element-2625">✅ Remembering a client's daughter just started school.</li>
            <li data-id="element-2626">✅ Sending a voice note instead of a generic text.</li>
            <li data-id="element-2627">✅ Following up months later — without an agenda.</li>
          </ul>
          <p data-id="element-2628">
            Those little moments have led to opportunities I could never have
            "strategized" into existence.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2629">💡 Takeaway</h3>
          <p data-id="element-2630">
            Relationships aren't built at once. They're built brick by brick,
            through curiosity, empathy, and consistency.
          </p>
          <p data-id="element-2631">
            And in a world obsessed with growth hacks and shortcuts, the deepest
            relationships remain the ultimate unfair advantage.
          </p>
          <p className="mt-6 font-medium" data-id="element-2632">
            👉 What's one moment in your career when a simple, human gesture
            turned into a lasting relationship?
          </p>
        </div>,
    author: 'Andrew Igbineweka',
    date: 'August 15, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/62DL8VzUWoZSsys88qcCmf/speaking_anne.jpg',
    slug: 'art-science-relationship-building'
  }, {
    id: 2,
    title: 'How to Make the Most of Your First Networking Event',
    excerpt: 'Attending your first networking event can be intimidating. Here are five strategies to help you navigate it successfully.',
    content: <div className="space-y-4" data-id="element-2633">
          <p data-id="element-2634">
            Walking into a room full of strangers can be daunting, especially
            when you're expected to make meaningful connections. But with the
            right mindset and approach, your first networking event can be the
            beginning of valuable professional relationships.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2635">
            1. Set Clear Intentions
          </h3>
          <p data-id="element-2636">
            Before the event, decide what you want to achieve. Is it meeting
            three new people in your industry? Learning about a specific topic?
            Having a conversation with a particular speaker? Having clear goals
            helps focus your energy and measure success beyond collecting
            business cards.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2637">
            2. Prepare Your Introduction
          </h3>
          <p data-id="element-2638">
            Craft a concise, engaging introduction that conveys who you are and
            what you're passionate about. Practice until it feels natural, not
            rehearsed. Remember, the goal is conversation, not perfection.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2639">
            3. Ask Thoughtful Questions
          </h3>
          <p data-id="element-2640">
            Great networkers are great listeners. Prepare open-ended questions
            that show genuine interest in others. "What are you working on that
            excites you right now?" often yields more interesting conversations
            than "What do you do?"
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2641">
            4. Find the Connectors
          </h3>
          <p data-id="element-2642">
            In every room, there are people who know everyone. These connectors
            can introduce you to others who align with your interests or goals.
            How to spot them? They're often greeting multiple people or being
            approached frequently.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2643">
            5. Follow Up Meaningfully
          </h3>
          <p data-id="element-2644">
            Within 48 hours, reach out to people you connected with. Reference
            something specific from your conversation and suggest a clear next
            step if appropriate. A thoughtful follow-up is what transforms a
            brief meeting into a potential relationship.
          </p>
          <p className="mt-6 font-medium" data-id="element-2645">
            Remember, meaningful networking isn't about quantity—it's about
            quality. One authentic connection can be more valuable than a dozen
            surface-level interactions.
          </p>
        </div>,
    author: 'The Networking Salon Team',
    date: 'July 22, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gumJfCcrUgAhRz8naofitq/Network_tobi.jpg',
    slug: 'make-most-first-networking-event'
  }, {
    id: 3,
    title: 'Building Community in a Digital-First World',
    excerpt: 'As remote work becomes the norm, how do we maintain authentic human connections?',
    content: <div className="space-y-4" data-id="element-2646">
          <p data-id="element-2647">
            The shift to digital-first work environments has fundamentally
            changed how we build and maintain professional relationships. While
            technology has removed geographical barriers, it has created new
            challenges for fostering genuine community.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2648">The Digital Paradox</h3>
          <p data-id="element-2649">
            We're more connected than ever through digital tools, yet many
            professionals report feeling increasingly isolated. This paradox
            exists because while information exchange has become easier,
            emotional connection requires more intentional effort in virtual
            spaces.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2650">
            Creating Digital Rituals
          </h3>
          <p data-id="element-2651">
            Strong communities are built on shared rituals. In a digital
            environment, these might include:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2652">
            <li data-id="element-2653">
              Weekly virtual coffee chats with no agenda beyond connection
            </li>
            <li data-id="element-2654">
              Monthly skill-sharing sessions where team members teach something
              they're passionate about
            </li>
            <li data-id="element-2655">
              Quarterly virtual retreats focused on relationship-building
              alongside strategic planning
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2656">
            The Power of Vulnerability
          </h3>
          <p data-id="element-2657">
            Digital spaces can feel impersonal, making vulnerability even more
            powerful. Leaders who share challenges, ask for help, and
            acknowledge uncertainty create psychological safety that allows
            communities to deepen.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-2658">
            Hybrid Connection Models
          </h3>
          <p data-id="element-2659">
            The most resilient communities blend digital and in-person
            experiences. Consider:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2660">
            <li data-id="element-2661">
              Using digital tools for regular touchpoints and information
              sharing
            </li>
            <li data-id="element-2662">
              Reserving in-person gatherings for deeper relationship building
              and complex collaboration
            </li>
            <li data-id="element-2663">
              Creating "digital campfires" – smaller, more intimate online
              gatherings that foster closer connections
            </li>
          </ul>
          <p className="mt-6 font-medium" data-id="element-2664">
            The future belongs to organizations and leaders who can thoughtfully
            design community experiences that leverage both digital convenience
            and human connection. The goal isn't to replicate in-person
            experiences online, but to create new models of community that
            embrace the unique advantages of both worlds.
          </p>
        </div>,
    author: 'The Networking Salon Team',
    date: 'June 5, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9ciCFjhcNKhhvWPuNF4wxL/Network_Scoot.jpg',
    slug: 'building-community-digital-first-world'
  }];
  const post = blogPosts.find(post => post.slug === slug);
  if (!post) {
    return <div className="pt-20" data-id="element-2665">
        <div className="container mx-auto px-4 py-16 text-center" data-id="element-2666">
          <h1 className="text-3xl font-bold mb-4" data-id="element-2667">Blog Post Not Found</h1>
          <p className="mb-8" data-id="element-2668">
            The blog post you're looking for doesn't exist or has been moved.
          </p>
          <Link to="/blog" className="inline-flex items-center text-primary hover:underline" data-id="element-2669">
            <ArrowLeft size={16} className="mr-2" data-id="element-2670" />
            Back to Blog
          </Link>
        </div>
      </div>;
  }
  return <div className="pt-20" data-id="element-2671">
      <article data-id="element-2672">
        <div className="relative h-[40vh] min-h-[300px] mb-8" data-id="element-2673">
          <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-2674" />
          <div className="absolute inset-0 bg-black bg-opacity-40" data-id="element-2675"></div>
          <div className="absolute inset-0 flex items-end" data-id="element-2676">
            <div className="container mx-auto px-4 pb-12" data-id="element-2677">
              <Link to="/blog" className="inline-flex items-center text-white hover:underline mb-4" data-id="element-2678">
                <ArrowLeft size={16} className="mr-2" data-id="element-2679" />
                Back to Blog
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-2680">
                {post.title}
              </h1>
              <div className="flex items-center text-white" data-id="element-2681">
                <span data-id="element-2682">{post.author}</span>
                <span className="mx-2" data-id="element-2683">•</span>
                <span data-id="element-2684">{post.date}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-8 mb-16" data-id="element-2685">
          <div className="max-w-3xl mx-auto prose prose-lg" data-id="element-2686">{post.content}</div>
        </div>
        <div className="container mx-auto px-4 py-8 border-t border-gray-200" data-id="element-2687">
          <div className="max-w-3xl mx-auto" data-id="element-2688">
            <h3 className="text-2xl font-bold mb-6" data-id="element-2689">
              More from The Networking Salon
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2690">
              {blogPosts.filter(p => p.id !== post.id).slice(0, 3).map(relatedPost => <Link to={`/blog/${relatedPost.slug}`} key={relatedPost.id} className="block" data-id="element-2691">
                    <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-2692">
                      <div className="aspect-[16/9] relative" data-id="element-2693">
                        <img src={relatedPost.image} alt={relatedPost.title} className="w-full h-full object-cover" data-id="element-2694" />
                      </div>
                      <div className="p-4" data-id="element-2695">
                        <h4 className="font-bold text-secondary-dark" data-id="element-2696">
                          {relatedPost.title}
                        </h4>
                        <p className="text-sm text-secondary mt-1" data-id="element-2697">
                          {relatedPost.date}
                        </p>
                      </div>
                    </div>
                  </Link>)}
            </div>
          </div>
        </div>
      </article>
    </div>;
};